/**
 * Taxi valuation engine.
 *
 * Every generic "car value calculator" on the internet prices a private car,
 * and that is why they are useless here. A commercial taxi is a different asset:
 *
 *   • It runs 5–6× the annual distance of a private car, so age and odometer
 *     have to be depreciated as two separate things instead of one.
 *   • The RC says "transport". Buyers know it has spent its life on 12-hour
 *     shifts with three drivers, so the first-year drop is far steeper.
 *   • It has a legal expiry date. In NCR a diesel commercial vehicle is done at
 *     10 years and a petrol/CNG one at 15, and the market prices that cliff in
 *     two or three years *before* it arrives.
 *   • Its value is set by what a fleet operator will pay to earn with it, not by
 *     what a family will pay to be seen in it. Model demand is therefore its own
 *     term — a Dzire holds value a Verito never will.
 *
 * Everything below is a multiplier on a base value, so each factor stays
 * separately inspectable and the UI can show the seller exactly where the money
 * went. The engine is pure: same inputs, same output, no network, no clock
 * beyond the reference year the caller passes in.
 */

/**
 * Retained fraction of a model's CURRENT ex-showroom price, indexed by age in
 * years, for a first-owner vehicle in good condition running baseline commercial
 * kilometres with valid papers. Anchored against current ex-showroom (rather
 * than the original invoice) because that is how the used market actually
 * prices: a buyer weighs your 2020 Dzire against a new one on the road today.
 *
 * One curve cannot cover the whole fleet. A 10-year-old Dzire taxi is worth a
 * little over a lakh; a 10-year-old Innova with the same story is worth several,
 * because operators will rebuild an Innova and will not rebuild a Dzire. That
 * divergence widens with age, so it belongs in the curve rather than in the flat
 * `demand` multiplier — hence `muv` for the Innova/Fortuner class.
 *
 * `luxury` covers the wedding-fleet, hotel and corporate chauffeur cars BST also
 * lists, and it crosses the `car` curve rather than sitting under it. For six
 * years it falls faster: the warranty ends, one gearbox or air-suspension job
 * costs more than a Dzire is worth, and the pool of buyers who can absorb that
 * bill is small. After that it falls slower, because the thing it is being
 * compared against — an app-cab hatchback on a permit — is heading for a legal
 * scrap date while an A6 is only getting cheap.
 */
export const RETENTION_CURVES = {
  //         new    1yr   2yr   3yr   4yr    5yr    6yr    7yr    8yr    9yr    10yr   11yr   12yr   13yr   14yr   15yr+
  car: [0.80, 0.70, 0.60, 0.51, 0.43, 0.36, 0.30, 0.25, 0.21, 0.175, 0.145, 0.12, 0.10, 0.085, 0.07, 0.058],
  muv: [0.84, 0.76, 0.68, 0.61, 0.54, 0.48, 0.43, 0.38, 0.34, 0.30, 0.265, 0.23, 0.20, 0.175, 0.15, 0.13],
  van: [0.84, 0.76, 0.68, 0.60, 0.53, 0.47, 0.42, 0.37, 0.33, 0.29, 0.255, 0.22, 0.19, 0.165, 0.14, 0.12],
  bus: [0.84, 0.76, 0.68, 0.61, 0.55, 0.49, 0.44, 0.39, 0.35, 0.31, 0.275, 0.245, 0.215, 0.19, 0.17, 0.15],
  luxury: [0.76, 0.65, 0.56, 0.48, 0.41, 0.345, 0.295, 0.252, 0.216, 0.186, 0.16, 0.138, 0.119, 0.102, 0.088, 0.076],
};

/**
 * The same curve for a privately-registered car of the same model and age, used
 * only for the "this is what commercial use costs you" comparison. Sellers
 * consistently anchor on private-car resale numbers they have seen on OLX or
 * CarDekho, and showing the gap explicitly is faster than arguing about it.
 */
export const PRIVATE_RETENTION = [
  0.85, 0.78, 0.71, 0.64, 0.58, 0.52, 0.47, 0.42, 0.375, 0.335, 0.30, 0.27, 0.24, 0.215, 0.19, 0.17,
];

/**
 * Private-use comparison for the luxury class. A privately-owned A6 does not
 * hold value the way a privately-owned Dzire does either, so comparing a
 * chauffeur-driven one against the mass-market private curve would overstate
 * the commercial penalty and make the calculator look like it is inventing
 * numbers.
 */
export const PRIVATE_RETENTION_LUXURY = [
  0.84, 0.74, 0.65, 0.57, 0.50, 0.44, 0.385, 0.335, 0.29, 0.25, 0.215, 0.185, 0.16, 0.14, 0.12, 0.104,
];

/**
 * What a working vehicle of each type is expected to have on the odometer. The
 * odometer term prices the gap against this, not against a private car's
 * 12,000 km/year — a 3-year-old taxi with 1.8 lakh km is simply normal.
 *
 * The luxury baseline is the low one: a wedding or corporate chauffeur car does
 * event days and airport runs, not 12-hour app-cab shifts. Two lakh kilometres
 * is an ordinary week's work story for a Dzire and a red flag on an E-Class.
 */
export const USAGE_BASELINES = {
  car: { kmPerYear: 60000, maxExpectedKm: 400000, penaltyPer10k: 0.012 },
  muv: { kmPerYear: 70000, maxExpectedKm: 500000, penaltyPer10k: 0.009 },
  van: { kmPerYear: 45000, maxExpectedKm: 400000, penaltyPer10k: 0.010 },
  bus: { kmPerYear: 55000, maxExpectedKm: 500000, penaltyPer10k: 0.007 },
  luxury: { kmPerYear: 22000, maxExpectedKm: 220000, penaltyPer10k: 0.011 },
};

/** Odometer adjustment is capped both ways — beyond this it stops being credible. */
export const KM_ADJUST_LIMITS = { min: 0.80, max: 1.12 };

/**
 * End-of-life rules by city. NCR is the strict one: diesel commercial vehicles
 * are off the road at 10 years and petrol/CNG at 15, under the NGT/Supreme Court
 * order. Kolkata caps commercial vehicles at 15. Everywhere else a transport
 * vehicle can keep going on annual fitness renewals, so there is no hard cliff —
 * only the fitness cost, which the papers term already handles.
 *
 * `demand` is how strong the local used-taxi market is: a fleet city with
 * constant permit churn absorbs vehicles faster than a small-town market.
 */
export const CITIES = [
  { id: 'delhi', label: 'Delhi', region: 'NCR', demand: 1.0, ageCap: { diesel: 10, other: 15 } },
  { id: 'gurugram', label: 'Gurugram', region: 'NCR', demand: 1.0, ageCap: { diesel: 10, other: 15 } },
  { id: 'noida', label: 'Noida / Greater Noida', region: 'NCR', demand: 0.99, ageCap: { diesel: 10, other: 15 } },
  { id: 'ghaziabad', label: 'Ghaziabad', region: 'NCR', demand: 0.97, ageCap: { diesel: 10, other: 15 } },
  { id: 'faridabad', label: 'Faridabad', region: 'NCR', demand: 0.97, ageCap: { diesel: 10, other: 15 } },
  { id: 'mumbai', label: 'Mumbai', demand: 1.02, ageCap: null },
  { id: 'pune', label: 'Pune', demand: 1.0, ageCap: null },
  { id: 'bangalore', label: 'Bangalore', demand: 1.03, ageCap: null },
  { id: 'hyderabad', label: 'Hyderabad', demand: 1.01, ageCap: null },
  { id: 'chennai', label: 'Chennai', demand: 1.0, ageCap: null },
  { id: 'kolkata', label: 'Kolkata', demand: 0.96, ageCap: { diesel: 15, other: 15 } },
  { id: 'ahmedabad', label: 'Ahmedabad', demand: 0.99, ageCap: null },
  { id: 'surat', label: 'Surat', demand: 0.97, ageCap: null },
  { id: 'jaipur', label: 'Jaipur', demand: 0.97, ageCap: null },
  { id: 'lucknow', label: 'Lucknow', demand: 0.96, ageCap: null },
  { id: 'chandigarh', label: 'Chandigarh / Mohali', demand: 0.98, ageCap: null },
  { id: 'other-metro', label: 'Other metro / large city', demand: 0.97, ageCap: null },
  { id: 'other-town', label: 'Smaller town', demand: 0.93, ageCap: null },
];

export const getCityById = (id) => CITIES.find((c) => c.id === id) || CITIES[0];

/** Spellings sellers type that do not match a city label. */
const CITY_ALIASES = {
  gurugram: ['gurgaon'],
  noida: ['greater noida', 'gautam buddha nagar'],
  bangalore: ['bengaluru'],
  mumbai: ['navi mumbai', 'thane', 'bombay'],
  kolkata: ['calcutta', 'howrah'],
  chennai: ['madras'],
  delhi: ['new delhi', 'nct of delhi'],
};

/**
 * Resolve a free-text city (and state) from the listing form to a rule set.
 * Getting this wrong in NCR is expensive — the age caps only apply there — so
 * an unrecognised city in Delhi/Haryana/UP still falls back to its NCR
 * neighbour rather than to the generic profile.
 */
export function findCityId(city, state = '') {
  const c = String(city || '').toLowerCase().trim();
  const s = String(state || '').toLowerCase().trim();

  if (c) {
    const exact = CITIES.find((x) => x.label.toLowerCase().includes(c) || c.includes(x.label.toLowerCase().split(' ')[0]));
    if (exact) return exact.id;
    for (const [id, names] of Object.entries(CITY_ALIASES)) {
      if (names.some((n) => c.includes(n))) return id;
    }
  }
  if (s === 'delhi') return 'delhi';
  return 'other-metro';
}

/** Fuel, judged on running cost per km and buyer pool — not on refinement. */
export const FUEL_FACTORS = {
  'CNG (factory-fitted)': 1.06,
  'CNG (retro-fitted)': 1.0,
  Petrol: 0.94,
  Diesel: 1.0,
  Hybrid: 0.98,
  Electric: 0.85,
};

export const REGISTRATION_OPTIONS = [
  {
    id: 'commercial',
    label: 'Commercial — yellow plate, state permit',
    factor: 1.0,
    note: 'Standard taxi registration. This is what the base valuation assumes.',
  },
  {
    id: 'aitp',
    label: 'Commercial — All India Tourist Permit (AITP)',
    factor: 1.04,
    note: 'AITP vehicles sell to buyers in any state, so the buyer pool is wider.',
  },
  {
    id: 'private',
    label: 'Private (white plate) used for taxi work',
    factor: 1.12,
    note: 'A white-plate RC sells to private buyers too, and carries no permit or fitness burden.',
  },
];

export const CONDITION_OPTIONS = [
  { id: 'excellent', label: 'Excellent — no dents, interiors intact, full service history', factor: 1.08 },
  { id: 'good', label: 'Good — minor wear, everything works, serviced regularly', factor: 1.0 },
  { id: 'average', label: 'Average — visible dents/scratches, tyres and interiors due', factor: 0.9 },
  { id: 'poor', label: 'Needs work — engine, AC or bodywork needs money spent', factor: 0.78 },
];

export const PAPERS_OPTIONS = [
  { id: 'valid', label: 'All valid for 6+ months', factor: 1.0 },
  { id: 'expiring', label: 'Fitness / permit / insurance expiring within 3 months', factor: 0.96 },
  { id: 'expired', label: 'Fitness, permit or insurance already expired', factor: 0.9 },
];

export const OWNER_FACTORS = { 1: 1.0, 2: 0.95, 3: 0.91, 4: 0.87 };

/** BS6 became mandatory for new registrations from 1 April 2020. */
export const BS6_FROM_YEAR = 2020;
const BS4_DIESEL_FACTOR = 0.9;

/** Older bodystyles stop matching the current model they are priced against. */
const generationFactor = (age, discontinued) => {
  let f = age >= 12 ? 0.9 : age >= 8 ? 0.95 : 1.0;
  if (discontinued) f *= 0.92;
  return f;
};

/**
 * Value left once a vehicle is past its city's age cap. It is not scrap — there
 * is a real out-of-state market for these — but the seller loses every local
 * buyer and eats NOC, transfer and re-registration friction on the way out.
 */
const OUT_OF_REGION_FACTOR = 0.5;

/**
 * Ceiling and floor on the stacked adjustments. Six favourable answers in a row
 * should not compound into a number no buyer would pay, and six unfavourable
 * ones should not wipe out a vehicle that still drives.
 */
const MULTIPLIER_LIMITS = { min: 0.45, max: 1.35 };

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const retentionAt = (curve, age) => {
  const table = RETENTION_CURVES[curve] || RETENTION_CURVES.car;
  return table[clamp(Math.round(age), 0, table.length - 1)];
};

const privateRetentionAt = (age, curve = 'car') => {
  const table = curve === 'luxury' ? PRIVATE_RETENTION_LUXURY : PRIVATE_RETENTION;
  return table[clamp(Math.round(age), 0, table.length - 1)];
};

/** What to call this thing in the odometer explanation. */
const VEHICLE_NOUN = { bus: 'bus', van: 'van', luxury: 'chauffeur-driven car' };

/**
 * "a Dzire" but "an Ertiga" — and "an XUV700", because a leading letter that is
 * read aloud (XUV, LP, S-Presso) takes 'an' even though it is a consonant.
 */
export const article = (name) => {
  const s = String(name || '').trim();
  if (/^(8|11|18)\b/.test(s)) return 'an'; // "an 8-year-old", "an 11-year-old"
  return /^[aeiou]/i.test(s) || /^[fhlmnrsx](?![a-z])/i.test(s) ? 'an' : 'a';
};

/**
 * Kilometres a working vehicle of this type would normally show at this age.
 * Age 0 is counted as half a year, and the total is capped because vehicles do
 * slow down once they fall out of front-line fleet duty.
 */
export const expectedKmFor = (curve, age) => {
  const b = USAGE_BASELINES[curve] || USAGE_BASELINES.car;
  return Math.round(Math.min(b.kmPerYear * Math.max(age, 0.5), b.maxExpectedKm));
};

/** Round to the nearest ₹1,000 — false precision reads as a made-up number. */
const roundTo = (value, step = 1000) => Math.round(value / step) * step;

export const formatINR = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `₹${Math.round(value).toLocaleString('en-IN')}`;
};

/** ₹6,45,000 → "₹6.45 lakh"; big numbers are easier to read in lakhs. */
export const formatLakh = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (value < 100000) return formatINR(roundTo(value, 1000));
  return `₹${(value / 100000).toFixed(2)} lakh`;
};

/**
 * Value a commercial vehicle.
 *
 * @param {Object}  input
 * @param {Object}  input.model         record from data/valuationModels.js
 * @param {number}  input.year          year of registration
 * @param {number}  input.km            odometer reading
 * @param {string}  input.fuel          key of FUEL_FACTORS
 * @param {string}  input.cityId        id from CITIES
 * @param {number}  input.owners        number of owners so far (1-based)
 * @param {string}  input.registration  id from REGISTRATION_OPTIONS
 * @param {string}  input.condition     id from CONDITION_OPTIONS
 * @param {string}  input.papers        id from PAPERS_OPTIONS
 * @param {boolean} input.accident      major accident / structural repair
 * @param {number}  [input.newPrice]    manual ex-showroom price, for 'other'
 * @param {number}  [input.referenceYear] defaults to the current calendar year
 */
export function valueTaxi(input) {
  const {
    model,
    year,
    km,
    fuel,
    cityId,
    owners = 1,
    registration = 'commercial',
    condition = 'good',
    papers = 'valid',
    accident = false,
    newPrice: manualPrice,
    referenceYear = new Date().getFullYear(),
  } = input;

  const newPrice = model?.manualPrice ? Number(manualPrice) : model?.newPrice;
  if (!model || !newPrice || !year) return null;

  const age = clamp(referenceYear - Number(year), 0, 25);
  const curve = model.curve || 'car';
  const city = getCityById(cityId);
  const odometer = Number(km) || 0;

  // ── Base: current ex-showroom × commercial retention at this age ──────────
  const retention = retentionAt(curve, age);
  const base = newPrice * retention;

  const breakdown = [];
  const addFactor = (label, factor, detail) => {
    if (Math.abs(factor - 1) < 0.0005) return;
    breakdown.push({ label, factor, pct: (factor - 1) * 100, detail });
  };

  // ── Odometer, measured against what a working taxi should have done ───────
  const baseline = USAGE_BASELINES[curve] || USAGE_BASELINES.car;
  const expectedKm = expectedKmFor(curve, age);
  const deltaTenK = (odometer - expectedKm) / 10000;
  const kmFactor = clamp(1 - deltaTenK * baseline.penaltyPer10k, KM_ADJUST_LIMITS.min, KM_ADJUST_LIMITS.max);
  addFactor(
    'Odometer',
    kmFactor,
    odometer > expectedKm
      ? `${Math.round((odometer - expectedKm) / 1000)},000 km above the ~${Math.round(expectedKm / 1000)},000 km ${article(age)} ${age}-year-old commercial ${VEHICLE_NOUN[curve] || 'taxi'} would normally have run`
      : `${Math.round((expectedKm - odometer) / 1000)},000 km below the ~${Math.round(expectedKm / 1000)},000 km expected at this age — genuinely low for commercial use`,
  );

  // ── Fuel ─────────────────────────────────────────────────────────────────
  const fuelFactor = FUEL_FACTORS[fuel] ?? 1.0;
  addFactor('Fuel type', fuelFactor, `${fuel} — priced on running cost per km and how many operators want it`);

  // Pre-2020 diesels are BS4. Outside NCR they still sell, but into a market
  // that knows GRAP bans and re-registration refusals are only getting tighter.
  const isBs4Diesel = fuel === 'Diesel' && Number(year) < BS6_FROM_YEAR;
  if (isBs4Diesel) {
    addFactor('BS4 diesel', BS4_DIESEL_FACTOR, 'Registered before April 2020, so BS4 — restricted in NCR and increasingly elsewhere');
  }

  // ── Registration, ownership, condition, papers ───────────────────────────
  const reg = REGISTRATION_OPTIONS.find((r) => r.id === registration) || REGISTRATION_OPTIONS[0];
  addFactor('Registration', reg.factor, reg.note);

  const ownerFactor = OWNER_FACTORS[clamp(Number(owners) || 1, 1, 4)];
  addFactor('Ownership', ownerFactor, `${owners >= 4 ? '4th or later' : `${owners}${owners === 2 ? 'nd' : owners === 3 ? 'rd' : 'st'}`} owner`);

  const cond = CONDITION_OPTIONS.find((c) => c.id === condition) || CONDITION_OPTIONS[1];
  addFactor('Condition', cond.factor, cond.label);

  const pap = PAPERS_OPTIONS.find((p) => p.id === papers) || PAPERS_OPTIONS[0];
  addFactor('Fitness / permit / insurance', pap.factor, pap.label);

  if (accident) addFactor('Accident history', 0.9, 'Major accident or structural repair on record');

  // ── Market: model demand and local market strength ───────────────────────
  addFactor('Model demand', model.demand, `How hard operators in the used market chase ${article(model.model)} ${model.model}`);
  addFactor('Local market', city.demand, `Used commercial vehicle demand in ${city.label}`);

  const genFactor = generationFactor(age, model.discontinued);
  addFactor(
    'Generation',
    genFactor,
    model.discontinued
      ? 'Model is no longer sold new — parts and buyer familiarity both thin out'
      : 'Older bodystyle than the version on sale today',
  );

  // ── End-of-life: the term no private-car calculator has ──────────────────
  const notes = [];
  const fuelIsDiesel = fuel === 'Diesel';
  const cap = city.ageCap ? (fuelIsDiesel ? city.ageCap.diesel : city.ageCap.other) : null;
  const remainingLife = cap === null ? null : cap - age;
  let endOfLife = false;
  let eolFactor = 1;

  if (remainingLife !== null) {
    if (remainingLife <= 0) {
      endOfLife = true;
      eolFactor = OUT_OF_REGION_FACTOR;
      addFactor('Past the age cap', eolFactor, `Over the ${cap}-year limit in ${city.region || city.label} — sellable only outside the region, or to a scrapper`);
      notes.push({
        tone: 'warn',
        text: `A ${fuelIsDiesel ? 'diesel' : 'petrol/CNG'} commercial vehicle cannot ply in ${city.region || city.label} past ${cap} years. It can only be sold for transfer to a state that still allows it, or scrapped — which is why the figure above is a fraction of what the same vehicle fetches elsewhere.`,
      });
    } else if (remainingLife <= 3) {
      eolFactor = remainingLife === 3 ? 0.93 : remainingLife === 2 ? 0.82 : 0.65;
      notes.push({
        tone: 'warn',
        text: `Only ${remainingLife} year${remainingLife === 1 ? '' : 's'} of legal life left in ${city.region || city.label} (${cap}-year cap for ${fuelIsDiesel ? 'diesel' : 'petrol/CNG'}). Buyers discount hard at this point, and the discount deepens every month you hold it.`,
      });
      addFactor('Remaining legal life', eolFactor, `${remainingLife} year${remainingLife === 1 ? '' : 's'} left before the ${cap}-year ${city.region || city.label} cap`);
    }
  }

  // ── Put it together ──────────────────────────────────────────────────────
  const rawMultiplier = breakdown.reduce((acc, b) => acc * b.factor, 1);
  const multiplier = clamp(rawMultiplier, MULTIPLIER_LIMITS.min, MULTIPLIER_LIMITS.max);
  const raw = Math.max(base * multiplier, model.scrapFloor);
  const fair = roundTo(raw);

  // The spread is wide on purpose. Commercial vehicles trade on condition
  // inspection more than spec sheets, and quoting a single number to the rupee
  // is the fastest way to lose a seller's trust when the first offer differs.
  const low = roundTo(fair * 0.91);
  const high = roundTo(fair * 1.09);
  const dealerOffer = roundTo(fair * 0.85);
  const askingPrice = roundTo(fair * 1.07);

  // ── What waiting costs: same vehicle, one and two years further on ────────
  const projection = [0, 1, 2].map((offset) => {
    const futureAge = age + offset;
    const futureKm = odometer + baseline.kmPerYear * offset;
    const futureExpected = expectedKmFor(curve, futureAge);
    const futureKmFactor = clamp(
      1 - ((futureKm - futureExpected) / 10000) * baseline.penaltyPer10k,
      KM_ADJUST_LIMITS.min,
      KM_ADJUST_LIMITS.max,
    );
    let futureEol = 1;
    if (cap !== null) {
      const left = cap - futureAge;
      futureEol = left <= 0 ? OUT_OF_REGION_FACTOR : left === 1 ? 0.65 : left === 2 ? 0.82 : left === 3 ? 0.93 : 1;
    }
    const others = rawMultiplier / (kmFactor * eolFactor); // everything that does not move with time
    const futureMultiplier = clamp(others * futureKmFactor * futureEol, MULTIPLIER_LIMITS.min, MULTIPLIER_LIMITS.max);
    const value = Math.max(newPrice * retentionAt(curve, futureAge) * futureMultiplier, model.scrapFloor);
    return { yearsFromNow: offset, age: futureAge, km: futureKm, value: roundTo(value) };
  });

  // ── The commercial-vs-private gap, for the explainer ─────────────────────
  const privateRetention = privateRetentionAt(age, curve);

  return {
    fair,
    low,
    high,
    dealerOffer,
    askingPrice,
    age,
    newPrice,
    base: roundTo(base),
    retention,
    retentionPct: retention * 100,
    privateRetentionPct: privateRetention * 100,
    extraDepreciationPct: (privateRetention - retention) * 100,
    expectedKm,
    km: odometer,
    multiplier,
    breakdown,
    projection,
    notes,
    endOfLife,
    remainingLife,
    ageCap: cap,
    city,
    isScrapFloor: raw <= model.scrapFloor + 1,
  };
}

/**
 * Retention curves as chart-ready points — commercial against private, same
 * model, same money at the start.
 */
export function retentionSeries(curve = 'car', years = 15) {
  return Array.from({ length: years + 1 }, (_, age) => ({
    age,
    taxi: retentionAt(curve, age),
    private: privateRetentionAt(age, curve),
  }));
}
